import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useProfileStore } from '@/store/profileStore'
import { toast } from 'sonner'

export interface UserSettings {
  preferences: {
    currency: string
    theme?: 'light' | 'dark' | 'system'
    language?: string
  }
  categories: string[]
  notifications?: {
    email: boolean
    push: boolean
  }
}

// Define a ProfileSettings interface to match the shape of what's in the ProfileStore
interface ProfileSettings {
  preferences?: {
    currency?: string;
    theme?: string;
    language?: string;
  };
  notifications?: {
    transaction_created?: boolean;
    push_notifications?: boolean;
  };
}

// Define a ProfileStore interface to match the shape of the store
interface ProfileStore {
  settings: ProfileSettings | null;
  isLoading: boolean;
  error: string | null;
  updateSettings: (userId: string, data: Partial<ProfileSettings>) => Promise<void>;
  fetchSettings: (userId: string) => Promise<void>;
}

const DEFAULT_SETTINGS: UserSettings = {
  preferences: {
    currency: 'USD',
    theme: 'system',
    language: 'en'
  },
  categories: [
    'Food & Dining',
    'Shopping',
    'Transportation',
    'Bills & Utilities',
    'Entertainment',
    'Health & Fitness',
    'Travel',
    'Education',
    'Gifts & Donations',
    'Other'
  ],
  notifications: {
    email: true,
    push: true
  }
}

export function useUserSettings() {
  const { user } = useAuth()
  const { 
    settings: profileSettings, 
    isLoading, 
    error: profileError, 
    updateSettings,
    fetchSettings 
  } = useProfileStore() as ProfileStore
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setSettings(null)
      return
    }

    if (profileSettings) {
      // Map profile settings to user settings format
      const userSettings: UserSettings = {
        preferences: {
          currency: profileSettings.preferences?.currency || DEFAULT_SETTINGS.preferences.currency,
          theme: (profileSettings.preferences?.theme as 'light' | 'dark' | 'system') || DEFAULT_SETTINGS.preferences.theme,
          language: profileSettings.preferences?.language || DEFAULT_SETTINGS.preferences.language
        },
        categories: DEFAULT_SETTINGS.categories, // We'll keep using the default categories for now
        notifications: {
          email: profileSettings.notifications?.transaction_created || DEFAULT_SETTINGS.notifications!.email,
          push: profileSettings.notifications?.push_notifications || DEFAULT_SETTINGS.notifications!.push
        }
      }
      
      setSettings(userSettings)
    } else {
      setSettings(DEFAULT_SETTINGS)
    }
  }, [user, profileSettings])

  useEffect(() => {
    if (profileError) {
      setError(profileError)
    }
  }, [profileError])

  useEffect(() => {
    // Fetch settings when user changes
    if (user?.id) {
      fetchSettings(user.id)
    }
  }, [user?.id, fetchSettings])

  const updateUserSettings = async (newSettings: Partial<UserSettings>) => {
    if (!user?.id || !settings) return

    try {
      // Map user settings format to profile settings format
      const profileUpdates: Partial<ProfileSettings> = {}
      
      if (newSettings.preferences) {
        profileUpdates.preferences = {
          ...(profileSettings?.preferences || {}),
          ...newSettings.preferences
        }
      }
      
      if (newSettings.notifications) {
        profileUpdates.notifications = {
          ...(profileSettings?.notifications || {}),
          transaction_created: newSettings.notifications.email,
          push_notifications: newSettings.notifications.push
        }
      }
      
      await updateSettings(user.id, profileUpdates)
      
      // Update local state
      setSettings({
        ...settings,
        ...newSettings
      })
      
      toast.success("Settings updated successfully")
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update settings')
      toast.error("Failed to update settings")
      throw err
    }
  }

  return {
    settings: settings || DEFAULT_SETTINGS,
    isLoading,
    error,
    updateSettings: updateUserSettings
  }
} 